import {
  MonitoringDeviceRulesResponse,
  MonitoringDeviceFamily,
  MonitoringDeviceRow,
  MonitoringHealth,
  MonitoringIncidentRow,
  MonitoringIncidentState,
  MonitoringOverview,
  MonitoringRulesResponse,
  MonitoringRuleCode,
} from './types';

function normalizeDate(value: unknown): Date | null {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === 'object' && value !== null && 'value' in value) {
    const parsed = new Date((value as { value: string }).value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

export function normalizeMonitoringOverview(raw: any): MonitoringOverview {
  return {
    summary: {
      ...raw.summary,
      lastEvaluatedAt: normalizeDate(raw.summary?.lastEvaluatedAt),
    },
    devices: (raw.devices || []).map(
      (device: any) =>
        ({
          ...device,
          lastDataAt: normalizeDate(device.lastDataAt),
          updatedAt: normalizeDate(device.updatedAt) || new Date(),
          hasRuleOverride: Boolean(device.hasRuleOverride),
          ruleOverrideCount: Number(device.ruleOverrideCount || 0),
        } as MonitoringDeviceRow)
    ),
    incidents: (raw.incidents || []).map(
      (incident: any) =>
        ({
          ...incident,
          resolutionSummary: incident.resolutionSummary ?? null,
          resolutionDetails: incident.resolutionDetails ?? null,
          firstOpenedAt: normalizeDate(incident.firstOpenedAt) || new Date(),
          lastOpenedAt: normalizeDate(incident.lastOpenedAt) || new Date(),
          resolvedAt: normalizeDate(incident.resolvedAt),
          updatedAt: normalizeDate(incident.updatedAt) || new Date(),
          lastObservedAt: normalizeDate(incident.lastObservedAt) || new Date(),
        } as MonitoringIncidentRow)
    ),
    filters: raw.filters,
  };
}

export function normalizeMonitoringRulesResponse(raw: any): MonitoringRulesResponse {
  return {
    profiles: (raw.profiles || []).map((profile: any) => ({
      ...profile,
      updatedAt: normalizeDate(profile.updatedAt),
      rules: (profile.rules || []).map((rule: any) => ({
        ...rule,
      })),
    })),
    deviceSummaries: (raw.deviceSummaries || []).map((device: any) => ({
      ...device,
      hasOverride: Boolean(device.hasOverride),
      overrideRuleCount: Number(device.overrideRuleCount || 0),
    })),
    severities: raw.severities || [],
  };
}

export function normalizeMonitoringDeviceRulesResponse(raw: any): MonitoringDeviceRulesResponse {
  return {
    ...raw,
    override: raw.override
      ? {
          ...raw.override,
          updatedAt: normalizeDate(raw.override.updatedAt) || new Date(),
        }
      : null,
    typeProfile: {
      ...raw.typeProfile,
      updatedAt: normalizeDate(raw.typeProfile?.updatedAt),
    },
    resolvedProfile: {
      ...raw.resolvedProfile,
      updatedAt: normalizeDate(raw.resolvedProfile?.updatedAt),
    },
    resolvedRules: raw.resolvedRules || [],
  };
}

export interface MonitoringUiFilters {
  search: string;
  deviceFamily: 'ALL' | MonitoringDeviceFamily;
  deviceHealth: 'ALL' | MonitoringHealth;
  ruleCode: 'ALL' | MonitoringRuleCode;
  incidentState: 'ALL' | MonitoringIncidentState;
  showOnlyIssues: boolean;
  showInactive: boolean;
}

function matchesSearch(haystack: string[], search: string) {
  if (!search) {
    return true;
  }

  const normalized = search.trim().toLowerCase();
  return haystack.some((value) => value.toLowerCase().includes(normalized));
}

export function filterMonitoringDevices(
  devices: MonitoringDeviceRow[],
  filters: MonitoringUiFilters
) {
  return devices.filter((device) => {
    if (filters.deviceFamily !== 'ALL' && device.deviceFamily !== filters.deviceFamily) {
      return false;
    }

    if (!filters.showInactive && !device.isActiveSensor) {
      return false;
    }

    if (filters.showOnlyIssues && device.activeIncidentCount === 0) {
      return false;
    }

    if (filters.deviceHealth !== 'ALL' && device.currentHealth !== filters.deviceHealth) {
      return false;
    }

    return matchesSearch(
      [device.orgTitle, device.sensorTitle, device.deviceId, device.deviceFamily],
      filters.search
    );
  });
}

export function filterMonitoringIncidents(
  incidents: MonitoringIncidentRow[],
  filters: MonitoringUiFilters
) {
  return incidents.filter((incident) => {
    if (filters.deviceFamily !== 'ALL' && incident.deviceFamily !== filters.deviceFamily) {
      return false;
    }

    if (filters.ruleCode !== 'ALL' && incident.ruleCode !== filters.ruleCode) {
      return false;
    }

    if (filters.incidentState !== 'ALL' && incident.status !== filters.incidentState) {
      return false;
    }

    return matchesSearch(
      [
        incident.orgTitle,
        incident.sensorTitle,
        incident.deviceId,
        incident.summary,
        incident.details,
        incident.resolutionSummary || '',
        incident.resolutionDetails || '',
      ],
      filters.search
    );
  });
}

export function getHealthBadgeClass(health: MonitoringHealth) {
  switch (health) {
    case 'critical':
      return 'border-red-200 bg-red-100 text-red-900';
    case 'warning':
      return 'border-amber-200 bg-amber-100 text-amber-900';
    case 'inactive':
      return 'border-slate-200 bg-slate-100 text-slate-800';
    default:
      return 'border-emerald-200 bg-emerald-100 text-emerald-900';
  }
}

export function getIncidentStateBadgeClass(state: MonitoringIncidentState) {
  return state === 'active'
    ? 'border-red-200 bg-red-100 text-red-900'
    : 'border-slate-200 bg-slate-100 text-slate-800';
}
